import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { FileSignature, CalendarClock, RefreshCw, Clock } from "lucide-react";
import { studio, beDate, eur } from "@/lib/studioApi";
import { SectionCard } from "@/components/studio/StudioUI";

const Row = ({ to, title, meta, right, tone, testId }) => (
  <Link to={to} data-testid={testId} style={{ boxShadow: `inset 3px 0 0 ${tone}` }} className="flex items-center gap-3 rounded-xl border border-[#E2E7F7] bg-white px-3 py-2.5 transition-colors hover:border-[#C9D4F4] hover:bg-[#F6F8FD]">
    <div className="min-w-0 flex-1">
      <p className="truncate text-sm font-semibold text-[#0F2645]">{title}</p>
      <p className="truncate text-xs text-[#5C6A87]">{meta}</p>
    </div>
    <span className="shrink-0 font-mono text-xs font-semibold tabular-nums text-[#0F2645]">{right}</span>
  </Link>
);

export const ContractsWidget = () => {
  const [data, setData] = useState(null);
  useEffect(() => { studio.get("/contracts-dashboard").then(({ data: d }) => setData(d)).catch(() => setData({ active_count: 0 })); }, []);
  if (!data || !data.active_count) return null;
  const empty = !data.week.length && !data.to_renew.length && !data.pending_renewals.length;
  return (
    <SectionCard
      title="Contrats d'entretien"
      hint={`${data.active_count} contrat(s) actif(s)`}
      testId="cockpit-contracts"
      className="dv-card-in mt-4"
      action={<Link to="/studio/contrats" data-testid="cockpit-contracts-all" className="text-xs font-semibold text-[#3A5CC8] hover:underline">Tous les contrats</Link>}
    >
      {empty && <p className="text-sm text-[#5C6A87]" data-testid="cockpit-contracts-empty">Aucun entretien cette semaine, aucun contrat à renouveler.</p>}
      {data.week.length > 0 && (
        <div>
          <p className="mb-2 inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-[#5C6A87]"><CalendarClock className="h-3.5 w-3.5" /> Entretiens des 7 prochains jours</p>
          <div className="space-y-2">
            {data.week.map((c) => <Row key={c.id} to={`/studio/contrats/${c.id}`} testId={`cockpit-contract-week-${c.number}`} title={`${c.client_name} · ${c.equipment || c.title}`} meta={`${c.number}${c.site_city ? ` · ${c.site_city}` : ""}`} right={beDate(c.next_due)} tone="#3A5CC8" />)}
          </div>
        </div>
      )}
      {data.to_renew.length > 0 && (
        <div className={data.week.length ? "mt-4" : ""}>
          <p className="mb-2 inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-[#5C6A87]"><RefreshCw className="h-3.5 w-3.5" /> À renouveler</p>
          <div className="space-y-2">
            {data.to_renew.map((c) => <Row key={c.id} to={`/studio/contrats/${c.id}?renouveler=1`} testId={`cockpit-contract-renew-${c.number}`} title={`${c.client_name} · ${c.equipment || c.title}`} meta={`${c.number} · fin le ${beDate(c.end_date)}`} right={eur(c.total)} tone="#F59E0B" />)}
          </div>
        </div>
      )}
      {data.pending_renewals.length > 0 && (
        <div className={data.week.length || data.to_renew.length ? "mt-4" : ""}>
          <p className="mb-2 inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-[#5C6A87]"><Clock className="h-3.5 w-3.5" /> Renouvellements en attente de signature</p>
          <div className="space-y-2">
            {data.pending_renewals.map((c) => <Row key={c.id} to={`/studio/contrats/${c.id}`} testId={`cockpit-contract-pending-${c.number}`} title={`${c.client_name} · renouvelle ${c.renews_number}`} meta={`${c.number}${c.sent_at ? ` · envoyé le ${beDate(c.sent_at.slice(0, 10))}` : ""}`} right={eur(c.total)} tone="#93A0BC" />)}
          </div>
        </div>
      )}
      <p className="mt-3 inline-flex items-center gap-1 text-[11px] text-[#93A0BC]"><FileSignature className="h-3 w-3" /> Relance automatique du client 10 jours après l'envoi d'un renouvellement.</p>
    </SectionCard>
  );
};
